<template>
  <div id="universityCampaigns">
    <div class="sub-title">
      <span class="fs-24">{{ $utils("addCommasInNumber", campaignsCount) }}</span>
      {{ $t("happilyVillage.campaigns") }}
    </div>
    
    <!-- Preview Mode Banner -->
    <div class="preview-banner">
      <div class="preview-banner-content">
        <span class="preview-badge">Early Preview</span>
        <span class="preview-text">You have early access! This feature is coming soon to everyone.</span>
      </div>
    </div>
    
    <div class="flex-row-between mt-14">
      <div class="flex-center" style="gap: 12px;">
        <div class="relative">
          <el-input
            class="input-style"
            style="height: 40px; width: 300px;"
            :placeholder="$t('happilyVillage.Search')"
            @keyup.native="handleSearch"
            v-model="searchQuery"
            clearable
          ></el-input>
        </div>
        <el-select
          v-model="statusFilter"
          :placeholder="$t('happilyVillage.All')"
          class="input-style"
          style="width: 180px;"
          @change="handleFilterChange"
        >
          <el-option label="All" value="all"></el-option>
          <el-option label="Active" value="active"></el-option>
          <el-option label="Upcoming" value="upcoming"></el-option>
          <el-option label="Ended" value="ended"></el-option>
        </el-select>
      </div>
      <div class="flex-center">
        <el-button
          class="btn-add small"
          size="small"
          icon="el-icon-plus"
          @click="openCreateModal"
        >
          {{ $t("happilyVillage.Create_Campaign") }}
        </el-button>
      </div>
    </div>
    
    <div class="mt-12">
      <div class="university-table-wrapper">
        <el-table
          class="table-happily-village campaigns-table"
          :data="campaigns"
          style="width: 100%"
          :empty-text="loadingCampaigns ? ' ' : 'No campaigns'"
        >
          <el-table-column
            label="Title"
            width="280"
            prop="title"
          >
            <template slot-scope="scope">
              <div class="flex-center" style="gap: 10px;">
                <img
                  v-if="scope.row.iconKey"
                  :src="getIconPath(scope.row.iconKey)"
                  style="width: 28px; height: 28px; flex-shrink: 0;"
                  alt="icon"
                />
                <span class="campaign-title-link" @click="handleTitleClick(scope.row)">
                  {{ getDisplayText(scope.row.title) }}
                </span>
              </div>
            </template>
          </el-table-column>
          
          <el-table-column
            label="Type"
            width="90"
            prop="inputType"
          >
            <template slot-scope="scope">
              <span class="normal">{{ formatInputType(scope.row.inputType) }}</span>
            </template>
          </el-table-column>
          
          <el-table-column
            label="Period"
            width="180"
          >
            <template slot-scope="scope">
              <div class="normal">
                {{ formatPeriod(scope.row.timing.startDate, scope.row.timing.endDate) }}
              </div>
            </template>
          </el-table-column>
          
          <el-table-column
            label="Reward"
            width="130"
          >
            <template slot-scope="scope">
              <span class="normal">{{ formatReward(scope.row.reward) }}</span>
            </template>
          </el-table-column>
          
          <el-table-column
            label="Entry Cost"
            width="100"
          >
            <template slot-scope="scope">
              <div class="flex-center" style="gap: 4px;">
                <div class="coin relative" style="width: 16px; height: 16px;"></div>
                <span class="normal">{{ scope.row.entryCost || 0 }}</span>
              </div>
            </template>
          </el-table-column>
          
          <el-table-column
            label="Status"
            width="100"
          >
            <template slot-scope="scope">
              <span :class="`status-badge status-${getCampaignStatus(scope.row)}`">
                {{ formatStatus(getCampaignStatus(scope.row)) }}
              </span>
            </template>
          </el-table-column>
          
          <el-table-column
            label="Participants"
            width="120"
          >
            <template slot-scope="scope">
              <span 
                class="participant-link" 
                @click="openLeaderboard(scope.row)"
              >
                {{ getParticipantCount(scope.row.id) }}
              </span>
            </template>
          </el-table-column>
          
          <el-table-column
            label="Actions"
            width="190"
          >
            <template slot-scope="scope">
              <div class="action-links">
                <span class="action-link action-link-edit" @click="editCampaign(scope.row)">Edit</span>
                <span class="action-link action-link-summary" @click="openSummary(scope.row)">Summary</span>
                <span class="action-link action-link-archive" @click="archiveCampaign(scope.row)">Archive</span>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      
      <div class="center mt-20 mb-40 pt-20 pb-40" v-if="loadingCampaigns">
        <loading-even></loading-even>
      </div>
      
      <div class="flex-center pagination mt-10" v-if="!loadingCampaigns && campaigns.length > 0">
        <el-pagination
          @current-change="handlePageChange"
          :current-page="currentPage"
          :page-size="10"
          :pager-count="5"
          layout="prev, pager, next"
          :total="campaignsCount"
        ></el-pagination>
        <span class="results-status">
          {{ campaigns.length }} {{ $t("happilyVillage.results_from") }}
          {{ $utils("addCommasInNumber", campaignsCount) }} {{ $t("happilyVillage.results") }}
        </span>
      </div>
    </div>
    
    <!-- Campaign Form Modal -->
    <university-campaign-form
      :visible="campaignModalVisible"
      :campaign="selectedCampaign"
      @close="closeCampaignModal"
      @submit="handleCampaignSubmit"
    />
    
    <!-- Leaderboard Modal -->
    <campaign-leaderboard-modal
      :visible="leaderboardModalVisible"
      :campaign="leaderboardCampaign"
      @close="closeLeaderboardModal"
    />

    <!-- Summary Modal -->
    <campaign-summary-modal
      :visible="summaryModalVisible"
      :campaign="summaryCampaign"
      @close="closeSummaryModal"
      @open-leaderboard="handleSummaryOpenLeaderboard"
    />
  </div>
</template>

<script>
import format from "date-fns/format";

export default {
  name: "university-campaigns",
  components: {
    loadingEven: require("../even/loading").default,
    universityCampaignForm: require("./university-campaign-form").default,
    campaignLeaderboardModal: require("./CampaignLeaderboardModal").default,
    campaignSummaryModal: require("./CampaignSummaryModal").default,
  },
  data() {
    return {
      searchQuery: "",
      statusFilter: "all",
      currentPage: 1,
      campaignModalVisible: false,
      selectedCampaign: null,
      leaderboardModalVisible: false,
      leaderboardCampaign: null,
      summaryModalVisible: false,
      summaryCampaign: null,
    };
  },
  computed: {
    allCampaigns() {
      return this.$store.getters["happilyvillageUniversity/campaigns"];
    },
    campaigns() {
      let filtered = this.allCampaigns;
      
      // Filter by status
      if (this.statusFilter && this.statusFilter !== 'all') {
        filtered = filtered.filter(campaign => {
          const status = this.getCampaignStatus(campaign);
          return status === this.statusFilter;
        });
      }
      
      // Filter by search query
      if (this.searchQuery && this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(campaign => {
          const title = this.getDisplayText(campaign.title, '').toLowerCase();
          return title.includes(query);
        });
      }
      
      return filtered;
    },
    campaignsCount() {
      return this.campaigns.length;
    },
    totalCampaignsCount() {
      return this.allCampaigns.length;
    },
    loadingCampaigns() {
      return this.$store.getters["happilyvillageUniversity/loadingCampaigns"];
    },
    campaignSummaries() {
      return this.$store.getters["happilyvillageUniversity/campaignSummaries"];
    },
  },
  created() {
    // Fetch campaigns from Firestore
    this.$store.dispatch("happilyvillageUniversity/fetchCampaigns");
    // Fetch campaign summaries for participant counts
    this.$store.dispatch("happilyvillageUniversity/fetchCampaignSummaries");
  },
  methods: {
    // Helper to extract text from multilingual objects
    getDisplayText(field, fallback = '') {
      if (!field) return fallback;
      // If it's a string (old format), return directly
      if (typeof field === 'string') return field || fallback;
      // If it's an object (multilingual format), prefer English then any available
      if (typeof field === 'object') {
        return field.en || field.th || Object.values(field).find(v => v) || fallback;
      }
      return fallback;
    },
    handleSearch() {
      // Filtering is reactive via computed property
      // Reset to first page when searching
      this.currentPage = 1;
    },
    handleFilterChange() {
      // Filtering is reactive via computed property
      // Reset to first page when filter changes
      this.currentPage = 1;
    },
    getCampaignStatus(campaign) {
      // Return status from campaign if it's archived
      if (campaign.status === 'archived') return 'archived';
      
      if (!campaign.timing) return 'active';
      
      const now = new Date();
      const startDate = this.parseDate(campaign.timing.startDate);
      const endDate = this.parseDate(campaign.timing.endDate);
      
      if (!startDate) return 'active';
      
      // Check if campaign hasn't started yet
      if (now < startDate) return 'upcoming';
      
      // Check if campaign has ended
      if (endDate && now > endDate) return 'ended';
      
      // Campaign is currently active
      return 'active';
    },
    formatStatus(status) {
      if (!status) return '';
      // Capitalize first letter
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    handlePageChange(page) {
      this.currentPage = page;
      // TODO(backend): Fetch page from backend
    },
    openCreateModal() {
      this.selectedCampaign = null;
      this.campaignModalVisible = true;
    },
    editCampaign(campaign) {
      this.selectedCampaign = { ...campaign };
      this.campaignModalVisible = true;
    },
    handleTitleClick(campaign) {
      // If campaign has started (active or ended), open leaderboard
      // Otherwise open edit modal
      if (this.hasCampaignStarted(campaign)) {
        this.openLeaderboard(campaign);
      } else {
        this.editCampaign(campaign);
      }
    },
    hasCampaignStarted(campaign) {
      if (!campaign || !campaign.timing || !campaign.timing.startDate) return false;
      
      const startDate = this.parseDate(campaign.timing.startDate);
      if (!startDate) return false;
      
      const now = new Date();
      return now >= startDate;
    },
    parseDate(dateValue) {
      if (!dateValue) return null;
      if (dateValue.toDate) return dateValue.toDate();
      if (dateValue instanceof Date) return dateValue;
      if (typeof dateValue === 'string') return new Date(dateValue);
      return null;
    },
    archiveCampaign(campaign) {
      this.$confirm(
        "This will archive the campaign. Continue?",
        "Warning",
        {
          confirmButtonText: "OK",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      ).then(() => {
        // Archive campaign in Firestore
        this.$store.dispatch("happilyvillageUniversity/archiveCampaign", campaign.id)
          .then(() => {
            this.$message({
              type: "success",
              message: "Campaign archived",
            });
          })
          .catch((error) => {
            this.$message({
              type: "error",
              message: `Failed to archive: ${error.message}`,
            });
          });
      });
    },
    closeCampaignModal() {
      this.campaignModalVisible = false;
      this.selectedCampaign = null;
    },
    handleCampaignSubmit(data) {
      // data is now { form, huddles } - same pattern as Town Hall and University Sessions
      const { form, huddles } = data;
      
      if (this.selectedCampaign && this.selectedCampaign.id) {
        // Edit existing campaign
        this.$store.dispatch("happilyvillageUniversity/updateCampaign", {
          id: this.selectedCampaign.id,
          form,
          huddles,
        })
          .then(() => {
            this.$message({
              type: "success",
              message: "Campaign updated successfully",
            });
            this.closeCampaignModal();
          })
          .catch((error) => {
            this.$message({
              type: "error",
              message: `Failed to update: ${error.message}`,
            });
          });
      } else {
        // Create new campaign - pass { form, huddles } object
        this.$store.dispatch("happilyvillageUniversity/createCampaign", { form, huddles })
          .then(() => {
            this.$message({
              type: "success",
              message: "Campaign created successfully",
            });
            this.closeCampaignModal();
          })
          .catch((error) => {
            this.$message({
              type: "error",
              message: `Failed to create: ${error.message}`,
            });
          });
      }
    },
    getIconPath(iconKey) {
      // Check if iconKey is a full URL (new format)
      if (iconKey && iconKey.startsWith('http')) {
        return iconKey;
      }

      // Legacy format: icon key that requires local asset
      try {
        return require(`@/assets/icons/${iconKey}.png`);
      } catch {
        return require("@/assets/icons/ic_star_24px.png");
      }
    },
    formatInputType(type) {
      const types = {
        quiz: "Quiz",
        count: "Count",
        auto_sync: "Auto Sync",
        auto_sync_steps: "Steps",
        auto_sync_sleep: "Sleep",
        auto_sync_screen: "Screen Time",
        auto_sync_exercise: "Exercise",
        auto_sync_distance: "Distance",
        multiple_selection: "Multiple Selection",
        yes_no: "Yes/No",
      };
      return types[type] || type;
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        return format(new Date(dateStr), "MMM D, YYYY");
      } catch {
        return dateStr;
      }
    },
    formatPeriod(startDateStr, endDateStr) {
      if (!startDateStr || !endDateStr) return "";
      try {
        const startDate = new Date(startDateStr);
        const endDate = new Date(endDateStr);
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();
        
        if (startYear === endYear) {
          // Same year: "Dec 10 - Jan 20"
          return `${format(startDate, "MMM D")} - ${format(endDate, "MMM D")}`;
        } else {
          // Different years: "Dec 10, 2025 - Jan 20, 2026"
          return `${format(startDate, "MMM D, YYYY")} - ${format(endDate, "MMM D, YYYY")}`;
        }
      } catch {
        return `${startDateStr} - ${endDateStr}`;
      }
    },
    formatReward(reward) {
      if (!reward) return "-";
      const types = {
        instant: "Instant Coins",
        charged: "Charged Coins",
        prize_pool: "Prize Pool",
      };
      return types[reward.type] || reward.type;
    },
    getParticipantCount(campaignId) {
      if (!campaignId) return 0;
      
      // First check campaign's own data - totalParticipants contains sign-ups count
      const campaign = this.allCampaigns.find(c => c.id === campaignId);
      if (campaign) {
        // totalParticipants = total sign-ups for the campaign
        if (campaign.totalParticipants !== undefined && campaign.totalParticipants > 0) {
          return campaign.totalParticipants;
        }
      }
      
      // Fall back to summary data (for active/ended campaigns with leaderboard)
      const summary = this.campaignSummaries[campaignId];
      if (summary) {
        if (summary.signupsCount !== undefined && summary.signupsCount > 0) {
          return summary.signupsCount;
        }
        if (summary.participantCount !== undefined) {
          return summary.participantCount;
        }
      }
      return 0;
    },
    openLeaderboard(campaign) {
      this.leaderboardCampaign = { ...campaign };
      this.leaderboardModalVisible = true;
    },
    closeLeaderboardModal() {
      this.leaderboardModalVisible = false;
      this.leaderboardCampaign = null;
    },
    openSummary(campaign) {
      this.summaryCampaign = { ...campaign };
      this.summaryModalVisible = true;
    },
    closeSummaryModal() {
      this.summaryModalVisible = false;
      this.summaryCampaign = null;
    },
    handleSummaryOpenLeaderboard(campaign) {
      this.closeSummaryModal();
      this.$nextTick(() => {
        this.openLeaderboard(campaign);
      });
    },
  },
};
</script>

<style scoped>
.sub-title {
  margin-top: 24px;
  margin-bottom: 16px;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.status-active {
  background-color: #E8F5E9;
  color: #2E7D32;
}

.status-upcoming {
  background-color: #E3F2FD;
  color: #1976D2;
}

.status-ended {
  background-color: #F5F5F5;
  color: #757575;
}

.status-draft {
  background-color: #FFF3E0;
  color: #E65100;
}

.status-archived {
  background-color: #FAFAFA;
  color: #9E9E9E;
}

.coin {
  background-image: url("~@/assets/images/ic_coin.png");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

/* Action Links */
.action-links {
  display: flex;
  align-items: center;
  gap: 16px;
}

.action-link {
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;
  white-space: nowrap;
}

.action-link-edit {
  color: #6C30CC;
}

.action-link-edit:hover {
  color: #5A28A8;
  text-decoration: underline;
}

.action-link-summary {
  color: #0D9488;
}

.action-link-summary:hover {
  color: #0F766E;
  text-decoration: underline;
}

.action-link-archive {
  color: #9CA3AF;
}

.action-link-archive:hover {
  color: #6B7280;
  text-decoration: underline;
}

/* Preview Banner */
.preview-banner {
  background: linear-gradient(135deg, #F9F5FF 0%, #F3EBFF 100%);
  border: 1px solid #E9D5FF;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 8px;
}

.preview-banner-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.preview-badge {
  background: linear-gradient(135deg, #7D5ABA 0%, #6C30CC 100%);
  color: white;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 4px 10px;
  border-radius: 6px;
  white-space: nowrap;
}

.preview-text {
  font-size: 13px;
  color: #6B7280;
  line-height: 1.4;
}

/* Campaign Title Link */
.campaign-title-link {
  font-weight: 700;
  font-size: 14px;
  line-height: 1.3;
  color: #111827;
  cursor: pointer;
  transition: color 0.15s ease;
}

.campaign-title-link:hover {
  color: #6C30CC;
}

/* Participant Link */
.participant-link {
  color: #6C30CC;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;
}

.participant-link:hover {
  color: #5A28A8;
  text-decoration: underline;
}
</style>

